import React, { FunctionComponent, useCallback, useEffect, useMemo } from 'react';
import { Button, Image } from 'antd';
import { Link } from 'react-router-dom';
import { useStore } from 'effector-react';
import moment from 'moment';
import cn from 'classnames';
import { CalendarOutlined } from '@ant-design/icons';
import { FULL_TIME_FORMAT } from '@config/format';
import IComment from '@interfaces/IComment';
import { getPath } from '@helpers/navigation';
import comments, { block, unblock } from '@stores/comments';
import componentContent from '@components/UserCommentCard/UserCommentCard.content.json';
import css from './UserCommentCard.module.less';
import useTranslate from '@hooks/useTranslate';
import { getComments } from '@stores/userComments';
import { useParams } from 'react-router';
import IUser from '@interfaces/IUser';
interface UserCommentCardProps {
  comment: IComment;
  isSubComment?: boolean;
}

const UserCommentCard: FunctionComponent<UserCommentCardProps> = (props) => {
  const { comment } = props;
  const { userId } = useParams<{ userId: IUser['id'] }>();
  const { preloaders } = useStore(comments);
  const content = useTranslate(componentContent);
  const isBlocking = preloaders.block === `block_${comment.id}`;
  const isUnBlocking = preloaders.unblock === `unblock_${comment.id}`;

  const blockComment = useCallback(() => {
    !isBlocking &&
      block({
        commentId: comment.id,
        preloaderId: `block_${comment.id}`,
      }).then(() => getComments({ userId: userId }));
  }, [isBlocking, comment.id]);

  const unBlockComment = useCallback(() => {
    !isUnBlocking &&
      unblock({
        commentId: comment.id,
        preloaderId: `unblock_${comment.id}`,
      }).then(() => getComments({ userId: userId }));
  }, [isUnBlocking, comment.id]);

  const messageText = useMemo(() => {
    if (comment?.gifUrl)
      return (
        <>
          <a
            href={comment.gifUrl}
          >
            {comment.gifUrl}
          </a>
          <br />
          <Image
            src={comment.gifUrl}
            width={120}
          />
        </>
      );

    return comment.text;
  }, [comment?.gifUrl, comment?.text]);

  return (
    <>
      <div className={cn(css.root, { [css.rootBlocked]: comment.isBlocked })}>
        <div className={css.content}>
          <div className={css.header}>
            <div
              className={cn(css.metaLine, css.meta)}
              style={{ justifyContent: 'space-between' }}
            >
              <div className={css.meta}>
                <span className={css.metaIcon}>
                  <CalendarOutlined />
                </span>
                <span>{moment(comment.whenCommented).format(FULL_TIME_FORMAT)}</span>
              </div>
              <Link
                className={cn(css.metaLineItem, 'clickable')}
                to={getPath('postDetail', { postId: comment.postId })}
              >
                {content.goToPost}
              </Link>
            </div>
          </div>
          <div className={css.body}>
            <div>
              <span>{messageText}</span>
            </div>
          </div>
          <div className={css.footer}>
            <div className={css.complaintsWrapper}>
              <div className={css.complaintsItem}>
                <span className={css.complaintsTitle}>{content.complained}:</span>{' '}
                <span className='clickable'>
                  {comment.complainsCount ??
                    ('complainsCount' in comment && comment.complainsCount)}
                </span>
              </div>

              <Button
                loading={isBlocking || isUnBlocking}
                size='small'
                onClick={comment.isBlocked ? unBlockComment : blockComment}
                type={comment.isBlocked ? 'default' : 'primary'}
              >
                {comment.isBlocked ? content.unblock : content.block}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserCommentCard;
